import { useRouter } from "next/router";
import { useCallback, useEffect, useState, useRef, useSyncExternalStore } from "react";
import { gaVisitorId } from "@/components/util";
import useCurrentAccount from "@/components/context/useCurrentAccount";
import { useApiForm } from "@/components/hooks/useApiForm";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Label } from "@/components/ui/Label";
import { Alert, AlertDescription } from "@/components/ui/Alert";
import { formatOtpRequestData, getPendingMonitorUrl, clearPendingMonitorUrl } from "@/utils/auth";
import { trackSignUp } from "@/utils/analytics";
import { executeRecaptchaWithCallback } from "@/utils/recaptcha";
import { getReferralCode } from "@/utils/referral";
import { getCurrentAttribution, buildOAuthUrl } from "@/utils/utmTracking";

const OAUTH_ERROR_MESSAGES: Record<string, string> = {
  google_auth_denied: "Google sign-in was cancelled.",
  google_auth_missing_params: "Invalid response from Google. Please try again.",
  google_auth_invalid_state: "Session expired. Please try again.",
  google_auth_token_error: "Failed to authenticate with Google. Please try again.",
  google_auth_userinfo_error: "Failed to get account info from Google. Please try again.",
  google_email_not_verified: "Your Google email address is not verified.",
  google_auth_account_error: "Failed to create account. Please try again.",
  apple_auth_denied: "Apple sign-in was cancelled.",
  apple_auth_missing_params: "Invalid response from Apple. Please try again.",
  apple_auth_invalid_state: "Session expired. Please try again.",
  apple_auth_token_error: "Failed to authenticate with Apple. Please try again.",
  apple_email_not_verified: "Your Apple email address is not verified.",
  apple_auth_account_error: "Failed to create account. Please try again.",
  rate_limited: "Too many attempts. Please try again later.",
};

export default function LoginForm() {
  const router = useRouter();
  const [email, setEmail] = useState("");
  const [otpCode, setOtpCode] = useState("");
  const [showOtp, setShowOtp] = useState(false);
  const { settings } = useCurrentAccount();
  const [recaptchaError, setRecaptchaError] = useState("");
  const [oauthError, setOauthError] = useState("");
  const emptySubscribe = useCallback(() => () => {}, []);
  const isMounted = useSyncExternalStore(
    emptySubscribe,
    () => true,
    () => false,
  );
  const otpInputRef = useRef<HTMLInputElement>(null);

  const [emailInitialized, setEmailInitialized] = useState(false);
  if (router.isReady && router.query.email && !emailInitialized) {
    setEmail(router.query.email as string);
    setEmailInitialized(true);
  }

  const [errorHandled, setErrorHandled] = useState(false);
  if (router.isReady && router.query.error && !errorHandled) {
    const errorCode = router.query.error as string;
    setOauthError(OAUTH_ERROR_MESSAGES[errorCode] || "An error occurred. Please try again.");
    setErrorHandled(true);
    router.replace("/accounts/login", undefined, { shallow: true });
  }

  // Request OTP API call
  const requestOtpForm = useApiForm({
    url: "/web_api/accounts/request_otp",
    method: "POST",
    onSuccess: () => {
      setShowOtp(true);
    },
    onError: () => {
      // Error is already handled by the hook
    },
  });

  // Verify OTP API call
  const verifyOtpForm = useApiForm({
    url: "/web_api/accounts/verify_otp",
    method: "POST",
    mutateKeys: ["/web_api/accounts/current"],
    onSuccess: async (data: any) => {
      if (data?.is_new_account) {
        trackSignUp("email");
      }

      // Check for return URL (e.g., from team invite)
      const returnUrl = router.query.returnUrl as string | undefined;
      if (returnUrl && returnUrl.startsWith("/")) {
        router.push(returnUrl);
        return;
      }

      // Check for pending URL
      const pendingUrl = getPendingMonitorUrl();

      if (pendingUrl) {
        await createMonitorWithUrl(pendingUrl);
      } else {
        router.push("/dashboard/monitors");
      }
    },
  });

  // Create monitor with URL (for pending URLs)
  const createMonitorForm = useApiForm({
    url: "/web_api/monitors/with_url",
    method: "POST",
    onSuccess: () => {
      clearPendingMonitorUrl();
      router.push("/dashboard/monitors");
    },
    onError: () => {
      // Still navigate to dashboard even if monitor creation fails
      router.push("/dashboard/monitors");
    },
  });

  const requestCode = useCallback(
    (e: any) => {
      e?.preventDefault();

      if (!email) return;

      setRecaptchaError("");
      setOauthError("");

      executeRecaptchaWithCallback(
        "login",
        async (recaptcha) => {
          const formData = formatOtpRequestData(email, recaptcha, gaVisitorId());
          await requestOtpForm.submit(formData);
        },
        () => {
          setRecaptchaError("Error with captcha verification. Please try again.");
        },
      );
    },
    [email, requestOtpForm],
  );

  const createMonitorWithUrl = useCallback(
    async (url: string) => {
      try {
        await executeRecaptchaWithCallback(
          "create_monitor",
          async (recaptcha) => {
            await createMonitorForm.submit({ url, recaptcha });
          },
          (error) => {
            console.error("Failed to create monitor:", error);
            router.push("/dashboard/monitors");
          },
        );
      } catch (error) {
        console.error("Failed to create monitor:", error);
        router.push("/dashboard/monitors");
      }
    },
    [createMonitorForm, router],
  );

  const verifyCode = useCallback(
    (e: any) => {
      e?.preventDefault();

      if (!email || !otpCode) return;

      setRecaptchaError("");
      setOauthError("");

      executeRecaptchaWithCallback(
        "login",
        async (recaptcha) => {
          // Get browser timezone safely
          let timezone: string | undefined;
          try {
            timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
          } catch (e) {
            // Silently ignore if timezone detection fails
            console.warn("Failed to detect timezone:", e);
          }

          const referralCode = getReferralCode();
          const utmParams = getCurrentAttribution();

          await verifyOtpForm.submit({
            email,
            recaptcha,
            otp_code: otpCode,
            vid: gaVisitorId(),
            timezone,
            referral_code: referralCode,
            ...utmParams, // Include UTM parameters
          });
        },
        () => {
          setRecaptchaError("Error with captcha verification. Please try again.");
        },
      );
    },
    [email, otpCode, verifyOtpForm],
  );

  const autoRequested = useRef(false);
  useEffect(() => {
    if (
      router.isReady &&
      router.query.email &&
      !showOtp &&
      !requestOtpForm.isLoading &&
      !autoRequested.current
    ) {
      autoRequested.current = true;
      const timeoutId = setTimeout(() => requestCode(null), 0);
      return () => clearTimeout(timeoutId);
    }
  }, [router.isReady, router.query.email, showOtp, requestOtpForm.isLoading, requestCode]);

  // Focus on OTP input when it becomes visible
  useEffect(() => {
    if (showOtp && otpInputRef.current) {
      // Small delay to ensure the input is fully rendered
      const timeoutId = setTimeout(() => {
        otpInputRef.current?.focus();
      }, 100);

      return () => clearTimeout(timeoutId);
    }
  }, [showOtp]);

  const onFormSubmit = showOtp ? verifyCode : requestCode;
  const isLoading = requestOtpForm.isLoading || verifyOtpForm.isLoading;
  const error = requestOtpForm.error || verifyOtpForm.error || recaptchaError || oauthError;

  return (
    <div className="space-y-6">
      {!showOtp && (settings?.google_oauth_enabled || settings?.apple_oauth_enabled) && (
        <div className="space-y-4">
          {settings?.google_oauth_enabled && (
            <a
              href={buildOAuthUrl("/web_api/accounts/google/auth")}
              className="inline-flex w-full cursor-pointer items-center justify-center gap-2 rounded-md border border-input bg-background px-4 py-2 text-sm font-medium shadow-sm transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
            >
              <svg className="h-4 w-4" viewBox="0 0 24 24">
                <path
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"
                  fill="#4285F4"
                />
                <path
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  fill="#34A853"
                />
                <path
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                  fill="#FBBC05"
                />
                <path
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                  fill="#EA4335"
                />
              </svg>
              Continue with Google
            </a>
          )}

          {settings?.apple_oauth_enabled && (
            <a
              href={buildOAuthUrl("/web_api/accounts/apple/auth")}
              className="inline-flex w-full cursor-pointer items-center justify-center gap-2 rounded-md border border-input bg-background px-4 py-2 text-sm font-medium shadow-sm transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
            >
              <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor">
                <path d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.48-3.24 0-1.44.62-2.2.44-3.06-.4C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
              </svg>
              Continue with Apple
            </a>
          )}

          <div className="flex items-center gap-2">
            <span className="flex-1 border-t" />
            <span className="text-xs uppercase text-muted-foreground">or continue with email</span>
            <span className="flex-1 border-t" />
          </div>
        </div>
      )}

      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <form onSubmit={onFormSubmit} className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="email">Email address</Label>
          <Input
            id="email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="email"
            required
            placeholder="name@example.com"
            className="w-full"
            readOnly={showOtp}
          />
        </div>

        {showOtp && (
          <div className="space-y-2">
            <Label htmlFor="otp">Verification code</Label>
            <Input
              ref={otpInputRef}
              id="otp"
              type="text"
              value={otpCode}
              onChange={(e) => setOtpCode(e.target.value)}
              autoComplete="one-time-code"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={6}
              required
              placeholder="Enter 6-digit code"
              className="w-full"
            />
            <p className="text-sm text-muted-foreground dark:text-muted-foreground/90">
              Check your email for the verification code
            </p>
          </div>
        )}

        <Button
          type="submit"
          disabled={!isMounted || isLoading || (showOtp && !otpCode)}
          className="w-full"
        >
          {isLoading ? "Processing..." : !showOtp ? "Continue" : "Sign in"}
        </Button>
      </form>
    </div>
  );
}
